import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from 'components/common/Button/Button'
import Link from 'components/common/Link/Link'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import api from 'helpers/api'
import style from 'styles/auth.module.scss'
import Code from 'components/common/Code/Code'
import { parseToken } from 'helpers/auth'
import ExclamationTriangle from 'components/assets/icons/ExclamationTriangle'
import PrefixedText from 'components/common/PrefixedText/PrefixedText'
import LoginLayout from 'components/Auth/LoginLayout/LoginLayout'
import { SubmitButton, AuthLink, AuthMessage, AuthAlt } from 'components/Auth/LoginLayout/fields'
import { isNewClientLogin } from 'config/loginDesign'

const SignUpConfirm = () => {
  const [message, setMessage] = useState('')
  const [codeError, setCodeError] = useState('')
  const [timeLeft, setTimeleft] = useState('')
  const [timeout, defineTimeout] = useState(undefined)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const tokenObj = parseToken(token)
  const id = tokenObj?.id
  const codeLength = Number(process.env.REACT_APP_CODE_SECURITY)


  // Validity timer
  const secondsLeft = Math.ceil((tokenObj?.exp * 1000 - Date.now()) / 1000)

  useEffect(() => {
    const timer = setInterval(() => {
      const min = Math.trunc(secondsLeft / 60)
      const sec = ("0"+Number(secondsLeft - (60 * min))).slice(-2)
      setTimeleft((min > 0 ? min+"min" : '')+sec+(min === 0 ? 's' : ''))
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, secondsLeft])


  // Define form
  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch
  } = useForm({
    criteriaMode: 'all',
    delayError: 0,
    defaultValues: {
      code: ''
    },
    mode: 'all',
  })
  const code = watch('code')


  const onSubmit = handleSubmit(async data => {
    const checkCode = async () => {
      try {
        const res = await api.post({
          url: '/auth/clients/confirm',
          data: { ...data, id },
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (res.success && res.response.accessToken){
          localStorage.setItem('accessToken', res.response.accessToken)
          navigate('/contracts')
        } else {
          setMessage('backError')
        }
      } catch (error) {
        if (error.message === "Code expired"){
          setCodeError('expired')
        } else if (error.message === "Wrong code"){
          setCodeError('wrongCode')
        } else if (error.message === "Too many attempts") {
          setCodeError('tooManyAttempts')
          if (timeout) clearTimeout(timeout)
          return
        } else if (error.message === "Too many requests") {
          setCodeError('tooManyRequests')
          return
        } else {
          setMessage('backError')
        }
      }
      clearMessages()
    }

    checkCode()
  })


  const getNewCode = async () => {
    setCodeError('')

    try {
      const res = await api.post({
        url: '/auth/clients/reset_confirm_code',
        data: { id },
        headers: { Authorization: `Bearer ${token}` }
      })

      if (res.success){
        setMessage('checkMail')
        navigate('/sign-up-confirm?token='+res.response.confirmToken)
      } else {
        setMessage('backError')
      }
    } catch (error) {
      if (error.message.startsWith('Wait')){
        setMessage('waitError')
      } else if (error.message === "Too many requests") {
        setCodeError('tooManyRequests')
        return
      } else {
        setMessage('backError')
      }
    }
    clearMessages()
  }


  const getNewMail = async () => {
    setCodeError('')

    try {
      const res = await api.post({
        url: '/auth/clients/resend_confirm_mail',
        data: { id },
        headers: { Authorization: `Bearer ${token}` }
      })

      if (res.success){
        setMessage('checkMail')
      } else {
        setMessage('backError')
      }
    } catch (error) {
      if (error.message.startsWith('Wait')){
        setMessage('waitError')
      } else if (error.message === "Too many requests") {
        setCodeError('tooManyRequests')
        return
      } else {
        setMessage('backError')
      }
    }
    clearMessages()
  }


  const clearMessages = () => {
    if (timeout){
      clearTimeout(timeout)
      defineTimeout(undefined)
    }
    defineTimeout(setTimeout( () => {
      setCodeError('')
      setMessage('')
      defineTimeout(undefined)
    }, 5000))
  }

  const messageSelection = () => {
    let msg = ''
    switch (message){
      case 'waitError':
        msg = t('main:auth.confirm.shouldWait')
        break;
      case 'checkMail':
        msg = t('main:auth.confirm.checkMail')
        break;
      default:
        msg = t('main:errorMessages.general')
        break;
    }
    return msg
  }

  const codeSelection = () => {
    let msg = ''
    switch (codeError){
      case 'wrongCode':
        msg = t('main:auth.confirm.wrongCode')
        break;
      case 'expired':
        msg = t('main:auth.confirm.expired')
        break;
      case 'tooManyAttempts':
        msg = t('main:auth.confirm.tooManyAttempts')
        break;
      case 'tooManyRequests':
        msg = t('main:auth.confirm.tooManyRequests')
        break;
      default:
        msg = t('main:errorMessages.general')
        break;
    }
    return msg
  }


  return !id || secondsLeft < 1
  ? <Navigate to="/sign-up" replace />
  : isNewClientLogin()
  ? (
    <LoginLayout
      title={t('main:auth.signUp.title')}
      subtitle={`${t('main:auth.confirm.code')}${timeLeft ? ` ${t('main:auth.confirm.validity', { timeLeft })}` : ''}`}
    >
      {message && <AuthMessage type={message === 'checkMail' ? 'success' : ''}>{messageSelection()}</AuthMessage>}
      <form onSubmit={onSubmit} noValidate>
        <Code
          id='code'
          label={t('main:auth.confirm.codeLabel')}
          length={codeLength}
          value={code}
          register={register('code', { required: true, minLength: codeLength })}
        />
        {codeError && (
          <div className="field-error-block">
            <AuthMessage>
              {codeSelection()}
              {['expired', 'tooManyAttempts'].includes(codeError) && <>
                <br /><AuthLink to="#" label={t('main:auth.confirm.getNewCode')} onClick={e => { e.preventDefault(); getNewCode(e) }} />
              </>}
            </AuthMessage>
          </div>
        )}
        <SubmitButton label={t('main:auth.signUp.register')} disabled={!isValid || ['tooManyAttempts', 'tooManyRequests'].includes(codeError)} />
      </form>
      <AuthAlt>
        <AuthLink to="#" label={t('main:auth.confirm.getNewMail')} onClick={e => { e.preventDefault(); getNewMail(e) }} />
      </AuthAlt>
    </LoginLayout>
  )
  : (
    <>
      <h2 className={style.title}>{t('main:auth.signUp.title')}</h2>

      {message &&
        <ResponseMessage
          type={message === 'checkMail' ? 'success' : ''}
          label={messageSelection()}
        />
      }

      <form onSubmit={onSubmit} className={style.form}>
        
        <p className={style.text}>
          {t('main:auth.confirm.code')}
          {timeLeft && ` ${t('main:auth.confirm.validity', {timeLeft})}`}
        </p>

        <Code
          id='code'
          label={t('main:auth.confirm.codeLabel')}
          length={codeLength}
          value={code}
          register={register('code', {
            required: true,
            minLength: codeLength
          })}
          darkBg
        />

        {codeError &&
          <div className={style['input-error']}>
            <PrefixedText
              text={<>
                {codeSelection()}
                {['expired', 'tooManyAttempts'].includes(codeError) && <>
                  <br/><Link
                    className={style.link}
                    label={t('main:auth.confirm.getNewCode')}
                    to={'#'}
                    onClick={getNewCode}
                    darkBg
                  />
                </>}
              </>}
              prefix={<ExclamationTriangle />}
            />
          </div>
        }

        <Button
          type='primary'
          label={t('main:auth.signUp.register')}
          className={style.button}
          onClick={handleSubmit}
          disabled={!isValid || ['tooManyAttempts', 'tooManyRequests'].includes(codeError)} 
        />

        <Link
          className={style.link}
          label={t('main:auth.confirm.getNewMail')}
          to={'#'}
          onClick={getNewMail}
          darkBg
        />

      </form>
    </>
  )
}

export default SignUpConfirm
